/** The `lenses` area: lenses: their page, configuration, section order and
 *  custom groups. Owned by the stage 2 slot views (docs/I18N.md §11). */
export const lenses = {
  // --- The Lenses page ---------------------------------------------------------
  'lenses.index.title': 'Views',
  'lenses.index.subEmpty': 'ways of looking',
  'lenses.index.saved': '{count} saved',
  'lenses.index.favorites': 'Favorites',
  'lenses.index.all': 'All views',
  'lenses.index.archived': 'Archived',
  /** The empty overview: the one sentence of the kind. */
  'lenses.index.empty':
    'A view is a saved filter and layout. It shows each person what they can see anyway.',
  /** A view card's meta line, after the layout: who maintains it, when it is the viewer. */
  'lenses.index.yours': 'yours',

  // --- A lens's page -----------------------------------------------------------
  'lenses.page.count': { one: '{count} item', other: '{count} items' },
  'lenses.surface.empty': 'Nothing you can see matches this view.',
  /** The list rail's footer when the list holds fewer rows than match
   *  (docs/LENSES.md "The listing budget"): how many rows it shows of how many matched, and
   *  the link that widens the budget. */
  'lenses.budget.shown': '{shown} of {total}',
  'lenses.budget.more': 'more',
  'lenses.budget.moreLabel': 'Show {next} of {total}',

  /** The state word beside the name of an archived board, list or view, in
   *  its page's head. */
  'lenses.archived': 'Archived',

  // --- A retired lens (the tombstone) -----------------------------------------
  'lenses.tombstone.note':
    'This view was archived. Its config stays readable - fork it to keep looking this way.',
  'lenses.tombstone.criteria': 'Criteria',
  'lenses.tombstone.noCriteria': 'none - everything you can see',
  'lenses.tombstone.defaultRail': 'Default layout',
  /** The stored default rail, as the tombstone names it. */
  'lenses.tombstone.rail.list': 'list',
  'lenses.tombstone.rail.board': 'kanban',
  'lenses.tombstone.rail.map': 'map',
  'lenses.tombstone.rail.checklist': 'checklist',
  'lenses.tombstone.fork': 'Fork this view',

  // --- The rail switch ---------------------------------------------------------
  'lenses.rail.label': 'Layout',
  /** The compact rail trigger: `rail` is the current rail's name. */
  'lenses.rail.menu': 'Layout ({rail})',
  'lenses.rail.showAs': 'Show as',
  'lenses.rail.list': 'List',
  'lenses.rail.listTitle': 'Show as a list',
  'lenses.rail.board': 'Kanban',
  'lenses.rail.boardTitle': 'Show as a kanban',
  'lenses.rail.map': 'Map',
  'lenses.rail.mapTitle': 'Show on the map',
  'lenses.rail.checklist': 'Checklist',
  'lenses.rail.checklistTitle': 'Tick off as a checklist',

  // --- The board rail's lanes --------------------------------------------------
  'lenses.lanes.menu': 'Columns by',
  'lenses.lanes.assignee': 'Contributor',
  'lenses.lanes.group': 'Group',
  'lenses.lanes.unassigned': 'Unassigned',
  'lenses.lanes.ungrouped': 'Ungrouped',

  // --- The list rail's arrange menus -------------------------------------------
  'lenses.arrange.groupBy': 'Group by',
  'lenses.arrange.sortBy': 'Sort by',
  'lenses.arrange.orderManually': 'Order manually…',
  'lenses.arrange.editCustomGroups': 'Edit custom groups…',
  'lenses.arrange.customGroups': 'Custom groups…',
  'lenses.arrange.hiddenGroups': 'Hidden groups',
  'lenses.sort.due': 'Due date',
  // The checklist's default sort: the order its lines were given by hand.
  'lenses.sort.manual': 'Manual',

  // --- A group's own menu ------------------------------------------------------
  'lenses.section.moveUp': 'Move up',
  'lenses.section.moveDown': 'Move down',
  'lenses.section.hide': 'Hide group',
  'lenses.section.expandAll': 'Expand all',
  'lenses.section.collapseAll': 'Collapse all',
  'lenses.section.resetOrder': 'Reset order',

  // --- The dialogs' buttons ----------------------------------------------------
  'lenses.dialog.cancel': 'Cancel',
  'lenses.dialog.apply': 'Apply',

  // --- The manual-order dialog -------------------------------------------------
  'lenses.order.title': 'Order groups',
  'lenses.order.loading': 'Loading groups…',
  'lenses.order.empty': 'No groups: nothing in this view falls into one.',
  'lenses.order.groups': 'Groups',
  'lenses.order.move': 'Move "{name}"',
  'lenses.order.show': 'Show "{name}"',
  'lenses.order.hide': 'Hide "{name}"',

  // --- The custom groups editor ------------------------------------------------
  'lenses.customGroups.title': 'Custom groups',
  'lenses.customGroups.note':
    'A row shows under every group whose criteria it meets; rows no group takes go to Other.',
  'lenses.customGroups.tooLong':
    'These groups are too long to apply. Shorten a name or remove a criterion.',
  'lenses.customGroups.nameMissing': 'Name the group.',
  'lenses.customGroups.add': 'Add group',
  'lenses.customGroups.limit': {
    one: 'A view holds at most {count} group.',
    other: 'A view holds at most {count} groups.',
  },
  'lenses.customGroups.name': 'Name',
  'lenses.customGroups.criteria': 'Criteria',
  'lenses.customGroups.addCriteria': 'Add criteria…',
  /** A group row's controls; the `Unnamed` twins name a group with no name yet. */
  'lenses.customGroups.move': 'Move "{name}"',
  'lenses.customGroups.moveUnnamed': 'Move "group"',
  'lenses.customGroups.criteriaOf': 'Criteria of "{name}"',
  'lenses.customGroups.criteriaOfUnnamed': 'Criteria of "group"',
  'lenses.customGroups.remove': 'Remove "{name}"',
  'lenses.customGroups.removeUnnamed': 'Remove "group"',

  // --- Configure view ----------------------------------------------------------
  'lenses.configure.label': 'Configure view',
  'lenses.configure.labelDirty': 'Configure view (unsaved changes)',
  'lenses.configure.titleClean': 'Configure this view: how it is saved and shared',
  'lenses.configure.titleDirty': 'Configure this view - it has unsaved changes',
  'lenses.configure.subtitleDirty': 'This view has unsaved changes',
  'lenses.configure.unsaved': 'Unsaved changes',
  'lenses.configure.unsavedNote':
    'Your filter and arrangement are live here but not saved. Keep them for everyone who opens this view, or take them into a view of your own.',
  'lenses.configure.nothingToSave':
    'Nothing to save - this view matches how it was left. Adjust the filter or arrangement and the changes land here.',
  'lenses.configure.update': 'Update view',
  'lenses.configure.fork': 'Fork',
  'lenses.configure.saveAsLens': 'Save as view',
  'lenses.configure.standard': 'Standard',
  /** `standard`: the standard's name, set apart. */
  'lenses.configure.deviates':
    'Your Work is your own, shaped away from {standard}. Restore puts it back to that standard; you can shape it again any time.',
  'lenses.configure.follows':
    'Your Work follows {standard}. Change the filter or arrangement and save, and it becomes your own.',
  'lenses.configure.restore': 'Restore standard',
  /** The section on a lens that copies it onto the viewer's own Work page. */
  'lenses.configure.work': 'Work',
  'lenses.configure.adoptNote':
    'Your Work takes a copy of the saved filter and arrangement of this view.',
  'lenses.configure.adoptCopy': 'Later changes to this view do not change your Work.',
  'lenses.configure.adopt': 'Use as my Work',
  /** Configure's first section on a saved view (and, beside the board's own
   *  heading, on a board): what it is, its name and its default layout
   *  (docs/VIEWS.md §2b "Managing a board"). */
  'lenses.configure.view': 'View',
  'lenses.configure.name': 'Name',
  'lenses.configure.defaultLayout': 'Default layout',
  /** The ways out, in the dialog's foot. */
  'lenses.configure.archive': 'Archive this view',
  'lenses.configure.leave': 'Leave this view',
  'lenses.configure.restoreView': 'Restore this view',

  // --- The deviation mark beside the page title --------------------------------
  'lenses.standardMark.label': 'Your own',
  'lenses.standardMark.title':
    'Shaped away from the standard {standard}. Configure view restores it.',
} as const;
